/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { EditOutlined, DeleteOutlined, CodeOutlined, FormOutlined } from '@ant-design/icons';
import { css } from '@emotion/react';
import { Box, Stack, Inline, Text, Hr, ToggleInput, IconButton, Draft } from '@resi-media/resi-ui';
import { JsonEditor } from 'json-edit-react';
import { SafeDataObject } from '@preview/helpers/SafeDataObject';
import { useJsonEditorTheme } from '@preview/hooks/useJsonEditorTheme';
import type { MockItem } from '../../types';

type _Props = {
  isActive: boolean;
  isConnected?: boolean;
  mock: MockItem;
  onDelete: (id: string) => void;
  onEdit: (mock: MockItem) => void;
  onToggle: (id: string) => void;
  onUpdate: (id: string, data: any) => void;
};

const EnhancedMockCard = ({ isActive, isConnected = false, mock, onDelete, onEdit, onToggle, onUpdate }: _Props) => {
  const jsonEditorTheme = useJsonEditorTheme();
  const [expanded, setExpanded] = useState(false);
  const [editMode, setEditMode] = useState<'fields' | 'json'>('fields');
  const [jsonError, setJsonError] = useState<string | null>(null);
  // Function to handle JSON editor changes
  const handleJsonChange = (data: any) => {
    setJsonError(null);
    try {
      const currentDataStr = JSON.stringify(mock.data);
      const newDataStr = JSON.stringify(data);

      if (currentDataStr !== newDataStr) {
        onUpdate(mock.id, data);
      }
    } catch (error) {
      console.error('Error updating JSON:', error);
      setJsonError('Invalid JSON format');
    }
  };

  const isArrayOfObjects = (value: any): boolean => {
    return (
      Array.isArray(value) &&
      value.length > 0 &&
      value.every((item) => item !== null && typeof item === 'object' && !Array.isArray(item))
    );
  };

  const handleFieldChange = (key: string, value: any) => {
    const dataWrapper = new SafeDataObject(mock.data);
    onUpdate(mock.id, dataWrapper.set(key, value).toJSON());
  };

  // Function to render field editors based on the mock data structure
  const renderFieldEditors = () => {
    const ignoreFields = ['googleAnalyticsId', 'customerId'];
    const dataWrapper = new SafeDataObject(mock.data);

    if (Object.keys(dataWrapper.toJSON()).length === 0) {
      return <Text>This data cannot be edited as fields. Please use JSON mode.</Text>;
    }

    return (
      <Stack gap="s">
        {Object.entries(dataWrapper.toJSON()).map(([key, value]: [string, any]) => {
          if (ignoreFields.includes(key)) {
            return null;
          }
          // Skip rendering complex nested objects
          if ((typeof value === 'object' && value !== null && !Array.isArray(value)) || isArrayOfObjects(value)) {
            return (
              <Box key={key}>
                <Text
                  css={css`
                    font-weight: bold;
                  `}
                >
                  {key}:
                </Text>
                <Text color="gray">Complex object (use JSON mode to edit)</Text>
              </Box>
            );
          }

          // Render simple values (strings, numbers, booleans)
          if (typeof value === 'string' || typeof value === 'number') {
            return (
              <Draft.FormField key={key} fieldLabel={key}>
                <Draft.TextInput
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (typeof value === 'number' && raw !== '') {
                      const parsed = Number(raw);
                      handleFieldChange(key, Number.isNaN(parsed) ? raw : parsed);
                    } else {
                      handleFieldChange(key, raw);
                    }
                  }}
                  value={String(value)}
                />
              </Draft.FormField>
            );
          }

          if (typeof value === 'boolean') {
            return (
              <Draft.FormField key={key} fieldLabel={key}>
                <ToggleInput checked={value} onChange={(e) => handleFieldChange(key, e.target.checked)} />
              </Draft.FormField>
            );
          }

          // Render arrays as comma-separated values
          if (Array.isArray(value)) {
            return (
              <Draft.FormField key={key} fieldLabel={`${key} (comma-separated)`}>
                <Draft.TextInput
                  onChange={(e) =>
                    handleFieldChange(
                      key,
                      e.target.value.split(',').map((s) => s.trim())
                    )
                  }
                  value={value.join(', ')}
                />
              </Draft.FormField>
            );
          }

          return null;
        })}
      </Stack>
    );
  };

  return (
    <Draft.Card
      key={mock.id}
      css={css`
        width: 100%;
        max-width: 368px;
        margin-bottom: 16px;
        border: ${isConnected ? '1px solid #52c41a' : 'none'};
        box-shadow: ${expanded ? '0 4px 12px rgba(0, 0, 0, 0.15)' : 'none'};
        transition: all 0.3s ease;
      `}
    >
      <Draft.CardSection
        css={css`
          cursor: pointer;
        `}
        onClick={() => setExpanded(!expanded)}
      >
        <Stack>
          <Inline alignItems="center" justifyContent="space-between">
            <Stack overflow="hidden" width="scale">
              <Inline alignItems="center" gap="s" justifyContent="space-between">
                <Text
                  css={css`
                    flex: 1 1 100%;
                  `}
                  truncate
                >
                  {mock.description || mock.endpoint}
                </Text>
                <Inline alignItems="center" gap="xs">
                  <IconButton label="Edit" onClick={() => onEdit(mock)}>
                    <EditOutlined />
                  </IconButton>
                  <IconButton colorVariant="negative" label="Delete" onClick={() => onDelete(mock.id)}>
                    <DeleteOutlined />
                  </IconButton>
                  <ToggleInput checked={isActive} onChange={() => onToggle(mock.id)} />
                </Inline>
              </Inline>
              <Inline alignItems="center" gap="s">
                <Text
                  css={css`
                    flex: 1 1 100%;
                  `}
                  truncate
                >
                  {mock.description ? mock.endpoint : null}
                </Text>
                {isConnected && (
                  <Box
                    css={css`
                      width: 10px;
                      height: 10px;
                      border-radius: 50%;
                      background-color: #52c41a;
                      margin-left: 10px;
                    `}
                    flexShrink={0}
                  />
                )}
              </Inline>
            </Stack>
          </Inline>
        </Stack>
      </Draft.CardSection>

      {expanded && (
        <Draft.CardSection>
          <Stack gap="xl">
            <Draft.FormField fieldLabel="Data View">
              <Inline gap="xs">
                <Draft.Button
                  isFullWidth
                  label="Fields"
                  onClick={() => setEditMode('fields')}
                  startNode={<FormOutlined />}
                  variant={editMode === 'fields' ? 'contained' : 'ghost'}
                />
                <Draft.Button
                  isFullWidth
                  label="JSON"
                  onClick={() => setEditMode('json')}
                  startNode={<CodeOutlined />}
                  variant={editMode === 'json' ? 'contained' : 'ghost'}
                />
              </Inline>
            </Draft.FormField>

            <Stack>
              {mock.description && <Text colorVariant="secondary">{mock.description}</Text>}

              <Hr
                css={css`
                  margin: 8px 0 16px;
                `}
              />

              <Box
                css={css`
                  max-height: 300px;
                  overflow-y: auto;
                `}
              >
                {editMode === 'fields' ? (
                  renderFieldEditors()
                ) : (
                  <React.Fragment>
                    <JsonEditor
                      data={mock.data}
                      id={`json-editor-${mock.id}`}
                      indent={1}
                      keySort
                      maxWidth="100%"
                      onError={(error) => setJsonError(error.error.message)}
                      rootName="data"
                      setData={handleJsonChange}
                      showCollectionCount="when-closed"
                      theme={jsonEditorTheme}
                    />

                    {jsonError && (
                      <Text
                        css={css`
                          color: #ff4d4f;
                          margin-top: 8px;
                        `}
                      >
                        {jsonError}
                      </Text>
                    )}
                  </React.Fragment>
                )}
              </Box>
            </Stack>
          </Stack>
        </Draft.CardSection>
      )}
    </Draft.Card>
  );
};

export default EnhancedMockCard;
